import { ref, computed, watch, type Component } from 'vue'
import { watchDebounced } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { useProjectsStore } from '@/views/Projects/store'
import { useApplicationsStore } from '@/views/Applications/store'
import { useRequirementsStore } from '@/views/Requirements/store'
import { useTicketsStore } from '@/views/Tickets/store'
import { useMeetingsStore } from '@/views/Meetings/store'
import { useOrganizationsStore } from '@/views/Organizations/store'
import { useDeliverablesStore } from '@/views/Deliverables/store'
import {
  FolderKanban,
  AppWindow,
  FileText,
  TicketCheck,
  CalendarDays,
  Building2,
  Boxes,
} from 'lucide-vue-next'

// ==========================================
// Types
// ==========================================

export type SearchEntityType = 'project' | 'application' | 'requirement' | 'ticket' | 'meeting' | 'organization' | 'deliverable'

export interface SearchResult {
  id: string
  entityType: SearchEntityType
  title: string
  subtitle: string
  badge?: string
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline'
  routeName: string
  routeParams: Record<string, string>
  score: number
}

export interface SearchGroup {
  entityType: SearchEntityType
  label: string
  icon: Component
  results: SearchResult[]
  totalCount: number
  listRouteName: string
}

export interface AdvancedFilters {
  entityTypes: SearchEntityType[]
  projectId: string
  dateFrom: string
  dateTo: string
}

// ==========================================
// Constants
// ==========================================

const RECENT_SEARCHES_KEY = 'torcaza_recent_searches'
const MAX_RECENT_SEARCHES = 5
const MAX_RESULTS_PER_GROUP = 4

const ENTITY_CONFIG: Record<SearchEntityType, { label: string; icon: Component; listRouteName: string }> = {
  project: { label: 'Proyectos', icon: FolderKanban, listRouteName: 'projects' },
  application: { label: 'Aplicaciones', icon: AppWindow, listRouteName: 'applications' },
  requirement: { label: 'Requerimientos', icon: FileText, listRouteName: 'requirements' },
  ticket: { label: 'Tickets', icon: TicketCheck, listRouteName: 'tickets' },
  meeting: { label: 'Reuniones', icon: CalendarDays, listRouteName: 'projects' },
  organization: { label: 'Organizaciones', icon: Building2, listRouteName: 'organizations' },
  deliverable: { label: 'Entregables', icon: Boxes, listRouteName: 'deliverables' },
}

// ==========================================
// Scoring
// ==========================================

function scoreMatch(query: string, text: string): number {
  if (!text || !query) return 0

  const q = query.toLowerCase()
  const t = text.toLowerCase()

  // Exact match
  if (t === q) return 100

  // Starts with
  if (t.startsWith(q)) return 80

  // Contains (penalized by position)
  const idx = t.indexOf(q)
  if (idx > 0) return Math.max(60 - idx, 30)

  // Fuzzy: chars in order
  let qi = 0
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++
  }
  if (qi === q.length) return 20

  return 0
}

function bestScore(query: string, ...fields: (string | undefined)[]): number {
  let max = 0
  for (const field of fields) {
    if (field) {
      const s = scoreMatch(query, field)
      if (s > max) max = s
    }
  }
  return max
}

// ==========================================
// Composable
// ==========================================

export function useGlobalSearch() {
  const router = useRouter()

  // State
  const isOpen = ref(false)
  const searchQuery = ref('')
  const debouncedQuery = ref('')
  const isAdvancedMode = ref(false)
  const advancedFilters = ref<AdvancedFilters>({
    entityTypes: [],
    projectId: '',
    dateFrom: '',
    dateTo: '',
  })
  const recentSearches = ref<string[]>(loadRecentSearches())
  const isLoadingStores = ref(false)
  const loadedStores = ref(false)

  // Debounce search input
  watchDebounced(
    searchQuery,
    (val) => { debouncedQuery.value = val.trim() },
    { debounce: 250 },
  )

  // Lazy load stores when dialog opens
  watch(isOpen, async (open) => {
    if (open && !loadedStores.value) {
      isLoadingStores.value = true
      try {
        const projectsStore = useProjectsStore()
        const applicationsStore = useApplicationsStore()
        const requirementsStore = useRequirementsStore()
        const ticketsStore = useTicketsStore()
        const meetingsStore = useMeetingsStore()
        const organizationsStore = useOrganizationsStore()

        const fetches: Promise<void>[] = []

        if (projectsStore.projects.length === 0) {
          fetches.push(projectsStore.fetchProjects())
        }
        if (applicationsStore.applications.length === 0) {
          fetches.push(applicationsStore.fetchApplications())
        }
        if (requirementsStore.requirements.length === 0) {
          fetches.push(requirementsStore.fetchRequirements())
        }
        if (ticketsStore.tickets.length === 0) {
          fetches.push(ticketsStore.fetchTickets())
        }
        if (meetingsStore.meetings.length === 0) {
          fetches.push(meetingsStore.fetchMeetings())
        }
        if (organizationsStore.organizations.length === 0) {
          fetches.push(organizationsStore.fetchOrganizations())
        }

        await Promise.allSettled(fetches)
        loadedStores.value = true
      } catch {
        // Stores may partially load, that's fine
      } finally {
        isLoadingStores.value = false
      }
    }
  })

  // Reset state when closing
  watch(isOpen, (open) => {
    if (!open) {
      searchQuery.value = ''
      debouncedQuery.value = ''
      isAdvancedMode.value = false
    }
  })

  // ==========================================
  // Search functions per entity
  // ==========================================

  function isEntityTypeAllowed(type: SearchEntityType): boolean {
    const types = advancedFilters.value.entityTypes
    return types.length === 0 || types.includes(type)
  }

  function passesDateFilter(createdAt?: Date): boolean {
    const { dateFrom, dateTo } = advancedFilters.value
    if (!dateFrom && !dateTo) return true
    if (!createdAt) return true
    const ts = createdAt.getTime()
    if (dateFrom && ts < new Date(dateFrom).getTime()) return false
    if (dateTo && ts > new Date(dateTo + 'T23:59:59').getTime()) return false
    return true
  }

  function searchProjects(query: string): SearchResult[] {
    if (!isEntityTypeAllowed('project')) return []
    const store = useProjectsStore()
    return store.projects
      .map((p) => {
        const score = bestScore(query, p.name, p.description, p.key)
        return {
          id: p.id,
          entityType: 'project' as const,
          title: p.name,
          subtitle: [p.key, p.status].filter(Boolean).join(' · '),
          badge: p.status,
          badgeVariant: p.status === 'on_hold' ? 'destructive' as const : 'secondary' as const,
          routeName: 'project-detail',
          routeParams: { id: p.id },
          score,
          createdAt: p.createdAt,
        }
      })
      .filter((r) => r.score > 0)
      .filter((r) => passesDateFilter(r.createdAt))
      .filter((r) => {
        if (advancedFilters.value.projectId) return r.id === advancedFilters.value.projectId
        return true
      })
      .sort((a, b) => b.score - a.score)
  }

  function searchApplications(query: string): SearchResult[] {
    if (!isEntityTypeAllowed('application')) return []
    const store = useApplicationsStore()
    return store.applications
      .map((a) => {
        const score = bestScore(query, a.name, a.description)
        return {
          id: a.id,
          entityType: 'application' as const,
          title: a.name,
          subtitle: [a.platform, a.status].filter(Boolean).join(' · '),
          badge: a.status,
          badgeVariant: 'secondary' as const,
          routeName: 'application-detail',
          routeParams: { id: a.projectId, applicationId: a.id },
          score,
          projectId: a.projectId,
          createdAt: a.createdAt,
        }
      })
      .filter((r) => r.score > 0)
      .filter((r) => passesDateFilter(r.createdAt))
      .filter((r) => {
        if (advancedFilters.value.projectId) return r.projectId === advancedFilters.value.projectId
        return true
      })
      .sort((a, b) => b.score - a.score)
  }

  function searchRequirements(query: string): SearchResult[] {
    if (!isEntityTypeAllowed('requirement')) return []
    const store = useRequirementsStore()
    return store.requirements
      .map((r) => {
        const score = bestScore(query, r.title, r.description, r.key)
        return {
          id: r.id,
          entityType: 'requirement' as const,
          title: r.title,
          subtitle: [r.key, r.type].filter(Boolean).join(' · '),
          badge: r.type,
          badgeVariant: r.type === 'bug' ? 'destructive' as const : 'outline' as const,
          routeName: 'requirement-detail',
          routeParams: { id: r.id },
          score,
          projectId: r.projectId,
          createdAt: r.createdAt,
        }
      })
      .filter((r) => r.score > 0)
      .filter((r) => passesDateFilter(r.createdAt))
      .filter((r) => {
        if (advancedFilters.value.projectId) return r.projectId === advancedFilters.value.projectId
        return true
      })
      .sort((a, b) => b.score - a.score)
  }

  function searchTickets(query: string): SearchResult[] {
    if (!isEntityTypeAllowed('ticket')) return []
    const store = useTicketsStore()
    return store.tickets
      .map((t) => {
        const score = bestScore(query, t.title, t.description)
        return {
          id: t.id,
          entityType: 'ticket' as const,
          title: t.title,
          subtitle: [t.type, t.status].filter(Boolean).join(' · '),
          badge: t.status,
          badgeVariant: t.status === 'open' ? 'destructive' as const : 'secondary' as const,
          routeName: 'ticket-detail',
          routeParams: { id: t.id },
          score,
          projectId: t.projectId,
          createdAt: t.createdAt,
        }
      })
      .filter((r) => r.score > 0)
      .filter((r) => passesDateFilter(r.createdAt))
      .filter((r) => {
        if (advancedFilters.value.projectId) return r.projectId === advancedFilters.value.projectId
        return true
      })
      .sort((a, b) => b.score - a.score)
  }

  function searchMeetings(query: string): SearchResult[] {
    if (!isEntityTypeAllowed('meeting')) return []
    const store = useMeetingsStore()
    return store.meetings
      .map((m) => {
        const score = bestScore(query, m.title, m.description)
        return {
          id: m.id,
          entityType: 'meeting' as const,
          title: m.title,
          subtitle: [m.meetingType, m.status].filter(Boolean).join(' · '),
          badge: m.status,
          badgeVariant: m.status === 'cancelled' ? 'destructive' as const : 'secondary' as const,
          routeName: 'meeting-detail',
          routeParams: { id: m.projectId, meetingId: m.id },
          score,
          projectId: m.projectId,
          createdAt: m.createdAt,
        }
      })
      .filter((r) => r.score > 0)
      .filter((r) => passesDateFilter(r.createdAt))
      .filter((r) => {
        if (advancedFilters.value.projectId) return r.projectId === advancedFilters.value.projectId
        return true
      })
      .sort((a, b) => b.score - a.score)
  }

  function searchOrganizations(query: string): SearchResult[] {
    if (!isEntityTypeAllowed('organization')) return []
    const store = useOrganizationsStore()
    return store.organizations
      .map((o) => {
        const score = bestScore(query, o.name, o.email)
        return {
          id: o.id,
          entityType: 'organization' as const,
          title: o.name,
          subtitle: o.email || '',
          badge: o.isActive ? 'Activa' : 'Inactiva',
          badgeVariant: o.isActive ? 'secondary' as const : 'destructive' as const,
          routeName: 'organization-detail',
          routeParams: { id: o.id },
          score,
          createdAt: o.createdAt,
        }
      })
      .filter((r) => r.score > 0)
      .filter((r) => passesDateFilter(r.createdAt))
      .sort((a, b) => b.score - a.score)
  }

  function searchDeliverables(query: string): SearchResult[] {
    if (!isEntityTypeAllowed('deliverable')) return []
    const store = useDeliverablesStore()
    return store.deliverables
      .map((d) => {
        const score = bestScore(query, d.name, d.description)
        return {
          id: d.id,
          entityType: 'deliverable' as const,
          title: d.name,
          subtitle: [d.type, d.status].filter(Boolean).join(' · '),
          badge: d.type,
          badgeVariant: 'secondary' as const,
          routeName: 'deliverable-detail',
          routeParams: { id: d.projectId, deliverableId: d.id },
          score,
          projectId: d.projectId,
          createdAt: d.createdAt,
        }
      })
      .filter((r) => r.score > 0)
      .filter((r) => passesDateFilter(r.createdAt))
      .filter((r) => {
        if (advancedFilters.value.projectId) return r.projectId === advancedFilters.value.projectId
        return true
      })
      .sort((a, b) => b.score - a.score)
  }

  // ==========================================
  // Computed groups
  // ==========================================

  const searchGroups = computed<SearchGroup[]>(() => {
    const query = debouncedQuery.value
    if (!query) return []

    const searchers: [SearchEntityType, (q: string) => SearchResult[]][] = [
      ['project', searchProjects],
      ['application', searchApplications],
      ['deliverable', searchDeliverables],
      ['requirement', searchRequirements],
      ['ticket', searchTickets],
      ['meeting', searchMeetings],
      ['organization', searchOrganizations],
    ]

    const groups: SearchGroup[] = []

    for (const [type, searchFn] of searchers) {
      const allResults = searchFn(query)
      if (allResults.length === 0) continue

      const config = ENTITY_CONFIG[type]
      groups.push({
        entityType: type,
        label: config.label,
        icon: config.icon,
        results: allResults.slice(0, MAX_RESULTS_PER_GROUP),
        totalCount: allResults.length,
        listRouteName: config.listRouteName,
      })
    }

    return groups
  })

  const totalResults = computed(() =>
    searchGroups.value.reduce((sum, g) => sum + g.totalCount, 0),
  )

  // ==========================================
  // Recent searches
  // ==========================================

  function loadRecentSearches(): string[] {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  }

  function saveRecentSearch(term: string) {
    const trimmed = term.trim()
    if (!trimmed) return

    const updated = [trimmed, ...recentSearches.value.filter((s) => s !== trimmed)]
      .slice(0, MAX_RECENT_SEARCHES)
    recentSearches.value = updated
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
  }

  function removeRecentSearch(term: string) {
    recentSearches.value = recentSearches.value.filter((s) => s !== term)
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recentSearches.value))
  }

  function clearRecentSearches() {
    recentSearches.value = []
    localStorage.removeItem(RECENT_SEARCHES_KEY)
  }

  // ==========================================
  // Navigation
  // ==========================================

  function navigateToResult(result: SearchResult) {
    saveRecentSearch(searchQuery.value)
    isOpen.value = false
    router.push({ name: result.routeName, params: result.routeParams })
  }

  function selectRecentSearch(term: string) {
    searchQuery.value = term
  }

  // ==========================================
  // Advanced filters helpers
  // ==========================================

  function toggleEntityType(type: SearchEntityType) {
    const types = advancedFilters.value.entityTypes
    const idx = types.indexOf(type)
    if (idx === -1) {
      types.push(type)
    } else {
      types.splice(idx, 1)
    }
  }

  function resetAdvancedFilters() {
    advancedFilters.value = {
      entityTypes: [],
      projectId: '',
      dateFrom: '',
      dateTo: '',
    }
  }

  return {
    // State
    isOpen,
    searchQuery,
    debouncedQuery,
    isAdvancedMode,
    advancedFilters,
    recentSearches,
    isLoadingStores,

    // Computed
    searchGroups,
    totalResults,

    // Actions
    navigateToResult,
    selectRecentSearch,
    saveRecentSearch,
    removeRecentSearch,
    clearRecentSearches,
    toggleEntityType,
    resetAdvancedFilters,
  }
}
